// @ts-nocheck
import { Dictionary } from "@insite/client-framework/Common/Types";
import QueryString, { parse } from "qs";

type U = Dictionary<string>;
type LiteralUnionKeys<T> = keyof T | (string & { any?: never });
type NumbersParameter<T> = [LiteralUnionKeys<T>, number?];

export interface GetQueryStringParseOptions<T> extends QueryString.IParseOptions {
    booleans?: LiteralUnionKeys<T>[];
    numbers?: Array<LiteralUnionKeys<T> | NumbersParameter<T>>;
    parsers?: { [K in LiteralUnionKeys<T>]?: (value: string) => any };
}

const parseBoolean = (value: string) => {
    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return undefined;
};

const parseNumber = (value: string, toFixed?: number) => {
    const result = parseFloat(value);

    if (Number.isNaN(result)) {
        return undefined;
    }

    if (typeof toFixed !== "undefined") {
        return Number(result.toFixed(toFixed));
    }

    return result;
};

const getBaseQueryString = <T>(locationSearch: string, options?: GetQueryStringParseOptions<T>) => {
    return (locationSearch ? parse(locationSearch, { ignoreQueryPrefix: true, ...options }) : {}) as Partial<T & U>;
};

const getQueryString = <T>(locationSearch: string, options?: GetQueryStringParseOptions<T>) => {
    const { booleans, numbers, parsers, ...qsOptions } = options || {};
    let decoderKey = "";
    let keysToDelete: string[] = [];

    const _numbers = numbers?.map(x => (typeof x === "string" ? [x] : x)) as NumbersParameter<T>[];

    const queryString = getBaseQueryString(locationSearch, {
        ...qsOptions,
        decoder: (str, defaultDecoder, charset, type) => {
            if (type === "key") {
                decoderKey = str;
            }

            if (type === "value" && decoderKey) {
                let parsedValue;
                let isParsed = false;

                const currentParser = parsers?.[decoderKey];
                if (currentParser) {
                    parsedValue = currentParser(str);
                    isParsed = true;
                }

                if (!isParsed && booleans?.includes(decoderKey)) {
                    parsedValue = parseBoolean(str);
                    isParsed = true;
                }

                if (!isParsed) {
                    const numberEntry = _numbers?.find(x => x[0] === decoderKey);

                    if (numberEntry) {
                        parsedValue = parseNumber(str, numberEntry[1]);
                        isParsed = true;
                    }
                }

                if (isParsed && typeof parsedValue === "undefined") {
                    keysToDelete.push(decoderKey);
                }

                decoderKey = "";

                if (isParsed) {
                    return parsedValue;
                }
            }

            return defaultDecoder(str);
        },
    });

    keysToDelete.forEach(x => delete queryString[x]);

    return queryString;
};

export default getQueryString;
